import { useGLTF } from '@react-three/drei'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Surface3D } from 'features/assetViz/objects/surfaces/Surface3D'
import { ComponentSurfaceWrappingMode } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { ComponentSurfaceMeshData, Vec3 } from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import * as THREE from 'three'
import { GeckoComponentRect, GeckoMeasurementUnit } from 'types'

interface GLTFModelProps {
  url: string
  onBoundsReady?: (bbox: THREE.Box3) => void
}

export const GLTFModel: React.FC<GLTFModelProps> = ({ url, onBoundsReady }) => {
  const { focusedCDF } = useAssetVizInternal()
  const { scene, nodes } = useGLTF(url)

  const interactiveObject = React.useMemo(
    () => nodes.Interactive_Object as THREE.Mesh | undefined,
    [nodes]
  )

  const { bounds, bbox } = React.useMemo(() => {
    if (!interactiveObject) {
      return { bounds: null, bbox: null }
    }

    // Calculate the bounding box based on the actual geometry
    const bbox = new THREE.Box3().setFromObject(interactiveObject)
    const size = bbox.getSize(new THREE.Vector3())

    // Create GeckoComponentRect based on the bounding box
    const modelBounds = GeckoComponentRect.CreateFromXYs(
      focusedCDF?.tags.component ?? interactiveObject.name ?? 'model',
      bbox.min.x,
      bbox.min.y,
      bbox.max.x,
      bbox.max.y,
      focusedCDF?.data.params.measurement_unit ?? GeckoMeasurementUnit.Feet,
      focusedCDF?.data.params.measurement_unit ?? GeckoMeasurementUnit.Feet
    )

    const bounds = focusedCDF?.data.geometry ?? modelBounds

    // Calculate scale based on actual size
    const scale: Vec3 = [size.x, size.y, size.z]

    return { bbox, modelBounds, scale, bounds }
  }, [focusedCDF, interactiveObject])

  const surfaceUserData = React.useMemo<ComponentSurfaceMeshData | undefined>(
    () =>
      bounds
        ? {
            bounds: bounds,
            doWrapX: true,
            doWrapY: true,
          }
        : undefined,
    [bounds]
  )

  const defaultMaterial = React.useMemo(
    () => new THREE.MeshStandardMaterial(),
    []
  )

  // Render non-interactive meshes with proper transforms
  const nonInteractiveMeshes = React.useMemo(() => {
    const meshes: React.ReactNode[] = []

    if (!bounds) {
      return meshes
    }

    const userData: ComponentSurfaceMeshData = {
      bounds: bounds,
      doWrapX: true,
      doWrapY: true,
    }

    scene.traverse((child) => {
      // Skip the Interactive_Object and non-mesh objects
      if (
        child.name === 'Interactive_Object' ||
        !(child instanceof THREE.Mesh)
      ) {
        return
      }

      // Get the material from the mesh itself, handling both single and array cases
      const meshMaterial: THREE.Material | THREE.Material[] = (() => {
        if (!child.material) {
          return defaultMaterial
        } else if (Array.isArray(child.material)) {
          // For multi-material meshes, clone the array to avoid mutation
          return child.material.slice()
        } else {
          return child.material
        }
      })()

      meshes.push(
        <mesh
          key={child.uuid}
          name={`noninteractive_mesh_${child.name}`}
          geometry={child.geometry}
          position={child.position}
          rotation={child.rotation}
          scale={child.scale}
          userData={userData}
          layers={RenderLayerID.VisibleNonCollidable}
          material={meshMaterial}
        />
      )
    })

    return meshes
  }, [scene, bounds, defaultMaterial])

  // Notify parent when bounds are ready for camera positioning
  React.useEffect(() => {
    if (bbox && onBoundsReady) {
      onBoundsReady(bbox)
    }
  }, [bbox, onBoundsReady])

  if (!bounds) {
    return null
  }

  return (
    <group>
      {/* Render all non-interactive nodes */}
      {nonInteractiveMeshes}

      {/* Render Interactive_Object as Surface3D if it exists */}
      {interactiveObject && surfaceUserData && (
        <group
          position={interactiveObject.position}
          rotation={interactiveObject.rotation}
          scale={interactiveObject.scale}>
          <Surface3D
            geometry={interactiveObject.geometry}
            bounds={surfaceUserData.bounds}
            wrapMode={ComponentSurfaceWrappingMode.ComponentNoWrap}
          />
        </group>
      )}
    </group>
  )
}
